// Блоки-этажи. Фасад определяется зданием (см. FACADES) и состоянием:
//  dark   — обычный этаж
//  lit    — перфект: окна горят
//  people — комбо ≥3: в окнах жители
//  golden — комбо 5: «Золотой этаж»
// Текстура повторяется по ширине: у срезанного блока окна честно обрезаны.
//
// Геометрия — RoundedBoxGeometry: фаска ловит свет на рёбрах, low-poly сразу
// выглядит дороже. Она отдаёт одну группу на весь меш, поэтому боковые грани
// и крышки разносим по группам вручную (см. splitFacadeGroups).
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

const FULL_WIDTH = 3.2; // ширина, на которую нарисован один тайл фасада

// Палитры фасадов реальных зданий (low-poly настроение, не фотореализм)
const FACADES = {
  etazhi: { wall: '#f2efe9', win: '#3d5466', band: '#e2231a', cols: 4, winW: 18, winH: 26, winY: 14 },
  khrush: { wall: '#cfa87a', win: '#4a4238', band: '#8a6a4a', cols: 3, winW: 16, winH: 22, winY: 16, balcony: '#b8946a' },
  panel: { wall: '#c8ccd2', win: '#3a4450', band: '#9aa2ac', cols: 4, winW: 20, winH: 22, winY: 16, seams: '#a8aeb6' },
  mgu: { wall: '#d8c9a8', win: '#3c3a30', band: '#b09a6a', cols: 5, winW: 12, winH: 30, winY: 12 },
  mahanakhon: { wall: '#2e3440', win: '#8fb8d8', band: '#1c2230', cols: 4, winW: 24, winH: 34, winY: 10, glass: true },
  federation: { wall: '#b8d8ec', win: '#5a88b8', band: '#8fb0cc', cols: 3, winW: 30, winH: 36, winY: 8, glass: true },
  lakhta: { wall: '#bfe0dc', win: '#4a9a94', band: '#8fc8c0', cols: 3, winW: 30, winH: 38, winY: 8, glass: true },
  taipei: { wall: '#7aa88a', win: '#2e5a4a', band: '#5a8a6a', cols: 4, winW: 22, winH: 32, winY: 10, glass: true },
  shanghai: { wall: '#ccd4dc', win: '#68809a', band: '#a8b4c0', cols: 3, winW: 32, winH: 36, winY: 8, glass: true },
  burj: { wall: '#e0e4ea', win: '#7a94ac', band: '#b8c0cc', cols: 5, winW: 14, winH: 36, winY: 8, glass: true },
};
FACADES.test = FACADES.etazhi;

// --- Скины: перекраска фасадов, открывается за суммарные звёзды ---
// Скин меняет ТОЛЬКО цвета (разметка окон остаётся у здания), золотой блок
// всегда золотой. Чистая косметика: сервер и античит не участвуют.
export const SKINS = [
  { id: 'default', title: 'Как есть', desc: 'родные фасады зданий', stars: 0, colors: null },
  { id: 'night', title: 'Неон-найт', desc: 'тёмный бетон, бирюзовый неон', stars: 5, colors: { wall: '#232838', win: '#5df0e0', band: '#161b28', seams: '#2e3448', balcony: '#2e3448', glass: true } },
  { id: 'sunset', title: 'Терракота', desc: 'глина и янтарные окна', stars: 10, colors: { wall: '#c96f4a', win: '#5a3020', band: '#a04f30', seams: '#b05e3c', balcony: '#a85838' } },
  { id: 'marble', title: 'Мрамор', desc: 'белый камень, золотой пояс', stars: 18, colors: { wall: '#efece4', win: '#3c4148', band: '#c9a44a', seams: '#ded9cd', balcony: '#d8d2c4' } },
  { id: 'etazhi', title: 'Фирменный', desc: 'белый с красным — цвета Этажей', stars: 27, colors: { wall: '#f6f4f0', win: '#2b3440', band: '#e2231a', seams: '#e8e4dd', balcony: '#e2231a' } },
];

let currentSkin = 'default';
export function setBlockSkin(id) {
  currentSkin = SKINS.some((s) => s.id === id) ? id : 'default';
}

const textureCache = {};
const geometryCache = {};

function facadeSpec(building) {
  const base = FACADES[building] || FACADES.etazhi;
  const skin = SKINS.find((s) => s.id === currentSkin);
  return skin && skin.colors ? { ...base, ...skin.colors } : base;
}

function drawFacade(building, variant) {
  const spec = facadeSpec(building);
  const canvas = document.createElement('canvas');
  // Рисуем в 2x от логических координат — чётче окна, меньше лесенок
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.scale(2, 2);

  ctx.fillStyle = variant === 'golden' ? '#e8c34a' : spec.wall;
  ctx.fillRect(0, 0, 128, 64);

  // Швы панелек
  if (spec.seams && variant !== 'golden') {
    ctx.fillStyle = spec.seams;
    ctx.fillRect(42, 0, 2, 64);
    ctx.fillRect(84, 0, 2, 64);
  }

  const step = 128 / spec.cols;
  for (let i = 0; i < spec.cols; i++) {
    const x = Math.round(i * step + (step - spec.winW) / 2);
    if (variant === 'dark') {
      ctx.fillStyle = spec.win;
      ctx.fillRect(x, spec.winY, spec.winW, spec.winH);
      // Блик на стекле высоток
      if (spec.glass) {
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.fillRect(x + 2, spec.winY + 2, Math.max(2, spec.winW * 0.25), spec.winH - 4);
      }
    } else {
      ctx.fillStyle = variant === 'golden' ? '#fff3c4' : '#ffd76a';
      ctx.fillRect(x, spec.winY, spec.winW, spec.winH);
      ctx.fillStyle = '#fff8dd';
      ctx.fillRect(x, spec.winY, spec.winW, 8);
      if (variant === 'people') {
        // Житель: голова + плечи, поднятая рука
        const cx = x + spec.winW / 2;
        const base = spec.winY + spec.winH;
        ctx.fillStyle = '#2b2b2b';
        ctx.beginPath();
        ctx.arc(cx, base - 10, 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(cx - 4.5, base - 7, 9, 7);
        ctx.fillRect(cx + 4, base - 17, 2.4, 8);
      }
    }
    // Балконы хрущёвки
    if (spec.balcony && variant !== 'golden' && i % 2 === 1) {
      ctx.fillStyle = spec.balcony;
      ctx.fillRect(x - 2, spec.winY + spec.winH - 4, spec.winW + 4, 6);
    }
  }

  ctx.fillStyle = variant === 'golden' ? '#b8860b' : spec.band;
  ctx.fillRect(0, 56, 128, 8);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  // Линейная фильтрация + анизотропия: гладкие окна под любым углом камеры
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4;
  tex.wrapS = THREE.RepeatWrapping;
  return tex;
}

function getFacadeTexture(building, variant) {
  const key = `${building}:${variant}:${currentSkin}`; // скин — часть ключа кеша
  if (!textureCache[key]) textureCache[key] = drawFacade(building, variant);
  return textureCache[key];
}

/**
 * Разносит треугольники на две группы: боковые грани (материал 0, фасад)
 * и верх/низ (материал 1, гладкая стена). RoundedBoxGeometry такого деления
 * не даёт — классифицируем по нормали и перекладываем треугольники.
 * Заодно запекаем псевдо-AO в vertex colors: низ блока темнее верха.
 */
function splitFacadeGroups(geometry, height) {
  const geo = geometry.index ? geometry.toNonIndexed() : geometry;
  const pos = geo.attributes.position.array;
  const nor = geo.attributes.normal.array;
  const uv = geo.attributes.uv ? geo.attributes.uv.array : null;

  const triCount = pos.length / 9;
  const side = [];
  const caps = [];
  for (let t = 0; t < triCount; t++) {
    // Средняя нормаль треугольника: |ny| близко к 1 → это крышка
    const ny = (nor[t * 9 + 1] + nor[t * 9 + 4] + nor[t * 9 + 7]) / 3;
    (Math.abs(ny) > 0.7 ? caps : side).push(t);
  }

  const order = side.concat(caps);
  const p = new Float32Array(pos.length);
  const n = new Float32Array(nor.length);
  const u = uv ? new Float32Array(uv.length) : null;
  const col = new Float32Array(pos.length); // vertex colors под псевдо-AO

  const half = height / 2;
  for (let i = 0; i < order.length; i++) {
    const src = order[i] * 9;
    const dst = i * 9;
    for (let k = 0; k < 9; k++) {
      p[dst + k] = pos[src + k];
      n[dst + k] = nor[src + k];
    }
    if (u) {
      const srcUv = order[i] * 6;
      const dstUv = i * 6;
      for (let k = 0; k < 6; k++) u[dstUv + k] = uv[srcUv + k];
    }
    // Псевдо-AO: чем ниже точка в блоке, тем темнее (стык этажей уходит в тень)
    for (let v = 0; v < 3; v++) {
      const y = pos[src + v * 3 + 1];
      const shade = 0.72 + 0.28 * ((y + half) / height); // 0.72 у пола → 1.0 у потолка
      col[dst + v * 3] = shade;
      col[dst + v * 3 + 1] = shade;
      col[dst + v * 3 + 2] = shade;
    }
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(p, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(n, 3));
  if (u) out.setAttribute('uv', new THREE.BufferAttribute(u, 2));
  out.setAttribute('color', new THREE.BufferAttribute(col, 3));
  out.addGroup(0, side.length * 3, 0);
  out.addGroup(side.length * 3, caps.length * 3, 1);
  if (geo !== geometry) geo.dispose();
  return out;
}

// Геометрия кешируется по округлённым размерам: блоки часто повторяются,
// а генерация фаски со скруглением не бесплатна.
function getBlockGeometry(width, height, depth) {
  const key = `${width.toFixed(2)}x${height.toFixed(2)}x${depth.toFixed(2)}`;
  if (!geometryCache[key]) {
    const radius = Math.min(width, height, depth) * 0.06; // ~фаска 2–3% ширины
    const base = new RoundedBoxGeometry(width, height, depth, 2, radius);
    geometryCache[key] = splitFacadeGroups(base, height);
    base.dispose();
  }
  return geometryCache[key];
}

/**
 * Блок-этаж здания. width может быть меньше полного — окна по краям обрежутся.
 */
export function createBlock(width, height, depth, variant = 'dark', building = 'etazhi') {
  const base = getFacadeTexture(building, variant);
  // Клон текстуры ради индивидуального repeat (картинка общая, недорого)
  const tex = base.clone();
  tex.needsUpdate = true;
  tex.repeat.x = width / FULL_WIDTH;
  tex.offset.x = (1 - tex.repeat.x) / 2;

  const spec = facadeSpec(building);
  const wallColor = variant === 'golden' ? 0xe8c34a : new THREE.Color(spec.wall).getHex();
  const facade = new THREE.MeshLambertMaterial({ map: tex, vertexColors: true });
  const plain = new THREE.MeshLambertMaterial({ color: wallColor, vertexColors: true });
  // Золотой этаж светится: emissive выше единицы ловится порогом bloom
  if (variant === 'golden') {
    facade.emissive.setRGB(0.55, 0.38, 0.05);
    plain.emissive.setRGB(0.55, 0.38, 0.05);
  }
  const mesh = new THREE.Mesh(getBlockGeometry(width, height, depth), [facade, plain]);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Полная утилизация меша: материалы + клоны текстур.
 * Геометрия общая (кеш по размерам) — её не трогаем.
 */
export function disposeMesh(mesh) {
  if (!mesh) return;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const m of mats) {
    if (!m) continue;
    if (m.map) m.map.dispose();
    m.dispose();
  }
  // Геометрии обломков и прочих одноразовых мешей помечены own=true
  if (mesh.geometry && mesh.geometry.userData.own) mesh.geometry.dispose();
}

/**
 * Обломок среза — кусок бетона без окон, падает и исчезает.
 */
export function createDebris(width, height, depth) {
  const geo = new THREE.BoxGeometry(width, height, depth);
  geo.userData.own = true;
  const mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: 0xdcd8d0, transparent: true }));
  mesh.castShadow = true;
  return mesh;
}

/**
 * Шпиль для финального этажа (МГУ, Тайбэй, Бурдж, Башня Этажей).
 */
export function createSpire(kind = 'needle') {
  const g = new THREE.Group();
  if (kind === 'star') {
    // Шпиль МГУ: ступенька + игла + звезда
    const base = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.5, 0.9), new THREE.MeshLambertMaterial({ color: 0xd8c9a8 }));
    base.position.y = 0.25;
    const needle = new THREE.Mesh(new THREE.ConeGeometry(0.16, 2.4, 6), new THREE.MeshLambertMaterial({ color: 0xb09a6a }));
    needle.position.y = 1.7;
    const star = new THREE.Mesh(new THREE.OctahedronGeometry(0.3, 0), new THREE.MeshLambertMaterial({ color: 0xffd24a, emissive: 0xaa7700 }));
    star.position.y = 3.05;
    g.add(base, needle, star);
  } else if (kind === 'flag') {
    // Флаг Этажей
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 2, 6), new THREE.MeshLambertMaterial({ color: 0x2b2b2b }));
    pole.position.y = 1;
    const flag = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 0.6), new THREE.MeshLambertMaterial({ color: 0xe2231a, side: THREE.DoubleSide }));
    flag.position.set(0.6, 1.6, 0);
    g.add(pole, flag);
  } else {
    const needle = new THREE.Mesh(new THREE.ConeGeometry(0.2, 3, 6), new THREE.MeshLambertMaterial({ color: 0xb8c0cc }));
    needle.position.y = 1.5;
    g.add(needle);
  }
  g.traverse((o) => { o.castShadow = true; });
  return g;
}

/**
 * Мягкая контактная тень под башней — дешёвый blob, «приземляет» постройку.
 */
export function createContactShadow(size = 6) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(64, 64, 4, 64, 64, 62);
  grad.addColorStop(0, 'rgba(0,0,0,0.5)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0.22)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(canvas);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(size, size),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.53; // чуть выше плиты, чтобы не мерцало
  mesh.renderOrder = 1;
  return mesh;
}
